import { useMemo } from "react";
import { Store } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAppDetail, useDiscoveredLocales } from "@/hooks/useApps";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";

/**
 * Global locale selector — header chrome, next to the Apps tree.
 *
 * Source: docs/product/experience-and-ui.md § Locale Selector (header);
 * ASOLOOP-UX doc item 1 (operator decision 2026-07-26): "Locale must be under
 * header selection and applies for all surfaces (signals, hypothesis,
 * experiments, creatives) — and we must also preserve 'All locales'".
 *
 * Single global control: the per-page locale switchers are retired, so this is
 * the ONLY locale affordance. Default is "All locales" (`null`), which shows
 * everything unfiltered on every surface.
 *
 * The option universe is per-app — the operator-curated `baseline_target_locales`
 * unioned with the auto-discovered set (ENG-LOCALE-AUTO-DISCOVERY), never
 * free-text. The selection resets to "All locales" on app switch (provider),
 * so a code from one app can never blank another app's surfaces.
 */
export default function LocaleSelect() {
  const { t } = useTranslation();
  const { selectedAppId, selectedLocale, setSelectedLocale } =
    useSelectedAppContext();
  const appDetail = useAppDetail(selectedAppId);
  const discovered = useDiscoveredLocales(selectedAppId);

  const locales = useMemo(() => {
    const set = new Set<string>();
    for (const code of appDetail.data?.baseline_target_locales ?? []) {
      set.add(code);
    }
    for (const row of discovered.data?.locales ?? []) set.add(row.locale);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [appDetail.data?.baseline_target_locales, discovered.data?.locales]);

  // With no app selected there is no locale universe to choose from; hide
  // rather than render an empty control (honest-empty, never a dead dropdown).
  if (selectedAppId === null || locales.length === 0) return null;

  return (
    <label className="flex items-center gap-1.5 text-xs font-medium text-muted">
      <Store size={14} aria-hidden="true" />
      <span className="sr-only">{t("layout.header.localeSelectLabel")}</span>
      <select
        value={selectedLocale ?? ""}
        onChange={(e) => setSelectedLocale(e.target.value || null)}
        className={cn(
          "rounded-md border border-divider bg-surface px-2 py-1 text-sm font-medium text-ink",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        )}
      >
        <option value="">{t("layout.header.allLocalesOption")}</option>
        {locales.map((locale) => (
          <option key={locale} value={locale}>
            {locale}
          </option>
        ))}
      </select>
    </label>
  );
}
